import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicHeader, PublicFooter } from "@/components/layouts/PublicLayout";
import { ArrowRight, Shield, Target, TrendingUp, CheckCircle, Info, BookOpen } from "lucide-react";
import { serviceList, SERVICE_IDS } from "@/content/serviceDescriptions";
import useServiceModal from "@/hooks/useServiceModal";
import ServiceInfoModal from "@/components/modals/ServiceInfoModal";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_ai-means-biz/artifacts/962is99v_A_I_Means_Business_R_Dale_Brunet.jpg";
const ABSTRACT_IMAGE = "https://customer-assets.emergentagent.com/job_712ba195-f8b2-4b91-a610-23f4aedf7ae0/artifacts/rje12dq8_AI_Means_Business_Image_2.jpg";
const FUTURISTIC_IMAGE = "https://customer-assets.emergentagent.com/job_712ba195-f8b2-4b91-a610-23f4aedf7ae0/artifacts/2l0e6jbd_AI_Means_Business_Image_3.jpg";
const ILLUSTRATION_IMAGE = "https://customer-assets.emergentagent.com/job_712ba195-f8b2-4b91-a610-23f4aedf7ae0/artifacts/nrzc9j03_AI_Means_Business_Image_4%20-%20Copy.jpg";
const BOOK_COVER_URL = "https://customer-assets.emergentagent.com/job_ai-value-pricing/artifacts/fnse5idx_AI_Survival_Guide_For_Humans_Front_Cover_Small.jpg";

const features = [
  {
    icon: Shield,
    title: "Risk Reduction",
    description: "Navigate A.I. disruption without betting your business on unproven technologies."
  },
  {
    icon: Target,
    title: "Strategic Clarity",
    description: "Cut through the noise with actionable insights tailored to your industry."
  },
  {
    icon: TrendingUp,
    title: "Decision Support",
    description: "Make confident A.I. decisions backed by experienced advisory guidance."
  }
];

const benefits = [
  "One-on-one advisory sessions",
  "Industry-specific A.I. landscape analysis",
  "Risk and opportunity assessment",
  "Clear, actionable recommendations",
  "No implementation required"
];

const LandingPage = () => {
  const { activeServiceId, isOpen, openServiceModal, closeServiceModal } = useServiceModal();

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-900 to-navy-950" />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${FUTURISTIC_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/80 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div className="stagger-children">
            <p className="text-primary font-medium mb-4 tracking-wide uppercase text-sm">
              A.I. Advisory for Established Businesses
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-extrabold text-white leading-none mb-6 tracking-tight">
              Navigate Artificial Intelligence
              <span className="block text-primary">With Confidence</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-lg">
              A.I. is transforming every industry. Don't get left behind—or waste resources on the wrong solutions. 
              Get expert guidance tailored to your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/get-started" data-testid="hero-cta">
                <Button className="bg-primary hover:bg-primary/90 text-white font-bold rounded-full px-8 py-6 text-lg shadow-[0_0_30px_rgba(217,119,6,0.4)] transition-all hover:scale-105">
                  Get Your Free A.I. Business Evaluation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/about" data-testid="hero-learn-more-link">
                <Button variant="outline" className="border-slate-700 text-slate-200 hover:border-primary/50 hover:text-primary rounded-full px-8 py-6 text-lg">
                  Learn More
                </Button>
              </Link>
            </div>
          </div>

          {/* Right: Image */}
          <div className="relative hidden lg:block">
            <div className="relative">
              <img
                src={HERO_IMAGE}
                alt="A.I. Business Advisory"
                className="rounded-2xl shadow-2xl max-w-[70%] ml-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="py-8 bg-navy-900 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
          <img src={ABSTRACT_IMAGE} alt="A.I." className="h-12 w-12 rounded-full opacity-60 hidden sm:block" />
          <p className="text-slate-400 font-medium text-center">
            Trusted advisory for businesses navigating A.I. transformation
          </p>
          <Link to="/about-founder" className="text-primary hover:text-primary/80 font-medium text-sm whitespace-nowrap">
            Meet Our Founder →
          </Link>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 bg-navy-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              A.I. Decisions Without Context Are Costly
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed">
              Some businesses will adopt A.I. strategically. Others will invest in solutions 
              that don't fit. The difference? Clarity about what actually matters for your business.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="glass p-8 rounded-2xl hover:border-primary/30 transition-all duration-500 group"
                  data-testid={`feature-${index}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-white mb-3">{feature.title}</h3>
                  <p className="text-slate-400">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Services Section */}
      <section className="py-24 bg-navy-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-primary font-medium mb-4 tracking-wide uppercase text-sm">
              Our Services
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Advisory Services for Every Stage
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed">
              From initial assessment to strategic planning, we provide the clarity you need 
              to make informed A.I. decisions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {serviceList.map((service) => (
              <div
                key={service.id}
                className="glass p-8 rounded-2xl hover:border-primary/30 transition-all duration-500 group flex flex-col"
                data-testid={`service-card-${service.id}`}
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <Info className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-400 mb-6 flex-1">
                  {service.shortSummary}
                </p>
                <button
                  onClick={() => openServiceModal(service.id)}
                  className="text-primary hover:text-primary/80 font-medium text-sm inline-flex items-center gap-1 transition-colors"
                  data-testid={`service-learn-more-${service.id}`}
                >
                  Learn more about this service
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-24 bg-navy-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src={ILLUSTRATION_IMAGE}
                alt="AI Advisory Process"
                className="rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <p className="text-primary font-medium mb-4 tracking-wide uppercase text-sm">
                The Bridge to Your A.I. Future
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
                Discovery & Viability Assessment
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Our foundational engagement gives you the clarity you need. We analyze your business, 
                assess the A.I. landscape in your industry, and deliver actionable recommendations.
              </p>
              <ul className="space-y-4 mb-8">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-slate-200">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link to="/get-started" data-testid="solution-cta">
                <Button className="bg-primary hover:bg-primary/90 text-white font-bold rounded-full px-8 py-4">
                  Get Your Free Evaluation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Ebook Promotional Section */}
      <section className="py-20 bg-navy-900/50 border-y border-slate-800">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-10">
            {/* Book Cover Image */}
            <div className="flex-shrink-0">
              <img 
                src={BOOK_COVER_URL} 
                alt="The A.I. Survival Guide For Humans" 
                className="w-48 md:w-56 h-auto rounded-lg shadow-2xl border border-slate-700"
              />
            </div>
            
            {/* Content */}
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                <BookOpen className="w-4 h-4" />
                Complimentary Resource
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4 tracking-tight">
                Start With Clarity. We'll Cover the Reading.
              </h2>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                Complete the Free AI Business Evaluation and receive a complimentary ebook 
                designed to help you understand AI, avoid common traps, and make better 
                decisions with confidence.
              </p>
              <Link to="/get-started" data-testid="ebook-promo-cta">
                <Button className="bg-white hover:bg-slate-100 text-navy-950 font-semibold rounded-full px-8 py-5 text-base transition-all hover:scale-105">
                  Get Your Free A.I. Business Evaluation
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <p className="text-slate-500 text-sm mt-4">
                No credit card required. Ebook delivered upon evaluation completion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-navy-950 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url(${ABSTRACT_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
            Clarity Before Commitment
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Understanding what A.I. means for your business starts with context—not sales pitches. 
            Take the first step toward informed decision-making.
          </p>
          <Link to="/get-started" data-testid="final-cta">
            <Button className="bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-navy-950 font-bold rounded-full px-10 py-6 text-lg shadow-[0_0_40px_rgba(217,119,6,0.5)] transition-all hover:scale-105">
              Get Your Free A.I. Business Evaluation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <PublicFooter />

      {/* Service Info Modal */}
      <ServiceInfoModal
        serviceId={activeServiceId}
        isOpen={isOpen}
        onClose={closeServiceModal}
      />
    </div>
  );
};

export default LandingPage;
